import React, { useState } from 'react';
// import { Copy, Check } from 'lucide-react';
import { Box, Button, Paper, Typography, Table, TableContainer, TableHead, TableRow, TableCell, TableBody, Divider, List, ListItem } from '@mui/material';

const CodeBlock = ({ code, language = 'text' }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };


    return (
        <Box sx={{ position: "relative", my: 2 }}>
            <Paper
                variant="outlined"
                sx={{
                    bgcolor: "grey.50",
                    borderRadius: 1,
                    overflow: "hidden",
                    borderColor: "grey.300"
                }}
            >
                {/* HEADER BAR */}
                <Box
                    sx={{
                        px: 1.5,
                        py: 0.5,
                        bgcolor: "grey.100",
                        borderBottom: "1px solid",
                        borderColor: "grey.300",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center"
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{ color: "grey.600", fontFamily: "monospace" }}
                    >
                        {language}
                    </Typography>

                    {/* Copy Button (optional to enable later) */}
                    <Button
                        onClick={handleCopy}
                        size="small"
                        sx={{
                            minWidth: 0,
                            px: 1,
                            fontSize: "0.7rem",
                            color: "grey.600",
                            "&:hover": { color: "grey.900" },
                        }}
                        startIcon={copied ? <p>✅</p> : <p>📋</p>}
                    >
                        {copied ? "Copied" : "Copy"}
                    </Button>
                </Box>

                {/* CODE AREA */}
                <Box
                    component="pre"
                    sx={{
                        p: 2,
                        m: 0,
                        overflowX: "auto",
                        fontSize: "0.875rem",
                    }}
                >
                    <code style={{ fontFamily: "monospace", color: "#333" }}>{code}</code>
                </Box>
            </Paper>
        </Box>
    );
};

export default function FlutterDoc() {
 

const slugify = (text) =>
    text
        .toLowerCase()
        .trim()
        .replace(/[^\w]+/g, "-")
        .replace(/(^-|-$)/g, "");

const BASE_URL = `${window.location.origin}/#/documentations/navigation-sdk`;

const formatInline = (text) => {
    // inline bold/italic/code
    text = text.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    text = text.replace(/__(.+?)__/g, "<strong>$1</strong>");
    text = text.replace(/\*(.+?)\*/g, "<em>$1</em>");
    text = text.replace(/_(.+?)_/g, "<em>$1</em>");
    text = text.replace(/`(.+?)`/g, '<code class="bg-gray-100 px-1 rounded">$1</code>');

    // 🔗 Auto-scroll links: [label](#section)
    text = text.replace(/\[([^\]]+)\]\(#([^)]+)\)/g, (m, label, id) => {
        return `<a href="#${id}" class="internal-anchor">${label}</a>`;
    });

    // 🔗 External links (leave your BASE_URL logic intact)
    text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (m, label, url) => {
        if (url.startsWith("#")) return m; // internal → handled above

        let finalUrl = url;

        if (url.startsWith("#/")) {
            finalUrl = `${BASE_URL}${url.replace(
                "#/documentations/navigation-sdk",
                ""
            )}`;
        } else if (url.startsWith("?#")) {
            finalUrl = `${BASE_URL}${url.replace("?", "")}`;
        }

        return `<a href="${finalUrl}" class="text-blue-600 hover:underline">${label}</a>`;
    });

    return text;
};

const parseMarkdown = (md) => {
    const lines = md.split("\n");
    const elements = [];
    let i = 0;
    let inCodeBlock = false;
    let codeContent = "";
    let codeLanguage = "";

    while (i < lines.length) {
        const line = lines[i];

        // CODE BLOCKS
        if (line.startsWith("```")) {
            if (!inCodeBlock) {
                inCodeBlock = true;
                codeLanguage = line.slice(3).trim() || "text";
                codeContent = "";
            } else {
                inCodeBlock = false;
                elements.push(
                    <CodeBlock key={i} code={codeContent.trim()} language={codeLanguage} />
                );
            }
            i++;
            continue;
        }

        if (inCodeBlock) {
            codeContent += line + "\n";
            i++;
            continue;
        }

        // HR
        if (/^(\-{3,}|\*{3,}|_{3,})$/.test(line)) {
            elements.push(<Divider key={i} sx={{ my: 3 }} />);
            i++;
            continue;
        }

        // TABLES (unchanged)
        if (line.includes("|") && line.trim().startsWith("|")) {
            const tableLines = [];
            let tableStart = i;
            while (i < lines.length && lines[i].includes("|")) {
                tableLines.push(lines[i]);
                i++;
            }

            if (tableLines.length >= 2) {
                const headers = tableLines[0]
                    .split("|")
                    .map((h) => h.trim())
                    .filter((h) => h);

                const rows = tableLines.slice(2).map((row) =>
                    row
                        .split("|")
                        .map((cell) => cell.trim())
                        .filter((cell) => cell)
                );

                elements.push(
                    <Box key={tableStart} sx={{ overflowX: "auto", my: 2 }}>
                        <TableContainer component={Paper} variant="outlined">
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        {headers.map((header, idx) => (
                                            <TableCell key={idx}>
                                                <span
                                                    dangerouslySetInnerHTML={{
                                                        __html: formatInline(header),
                                                    }}
                                                />
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map((row, rowIdx) => (
                                        <TableRow key={rowIdx}>
                                            {row.map((cell, cellIdx) => (
                                                <TableCell key={cellIdx}>
                                                    <span
                                                        dangerouslySetInnerHTML={{
                                                            __html: formatInline(cell),
                                                        }}
                                                    />
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                );
            }
            continue;
        }

        // HEADERS — with ID for auto-scroll
        if (line.startsWith("# ")) {
            const text = line.slice(2);
            elements.push(
                <Typography
                    key={i}
                    id={slugify(text)}
                    variant="h3"
                    fontWeight={600}
                    sx={{ mb: 1, mt: 4 }}
                >
                    {text}
                </Typography>
            );
        } else if (line.startsWith("## ")) {
            const text = line.slice(3);
            elements.push(
                <Typography
                    key={i}
                    id={slugify(text)}
                    variant="h4"
                    fontWeight={600}
                    sx={{ mb: 2, mt: 4 }}
                >
                    {text}
                </Typography>
            );
        } else if (line.startsWith("### ")) {
            const text = line.slice(4);
            elements.push(
                <Typography
                    key={i}
                    id={slugify(text)}
                    variant="h5"
                    fontWeight={600}
                    sx={{ mb: 2, mt: 3 }}
                >
                    {text}
                </Typography>
            );
        } else if (line.startsWith("#### ")) {
            const text = line.slice(5);
            elements.push(
                <Typography
                    key={i}
                    id={slugify(text)}
                    variant="h6"
                    fontWeight={600}
                    sx={{ mb: 1, mt: 2 }}
                >
                    {text}
                </Typography>
            );
        }

        // QUOTES
        else if (line.startsWith("> ")) {
            elements.push(
                <Paper
                    key={i}
                    sx={{
                        borderLeft: "4px solid #ccc",
                        p: 2,
                        my: 2,
                        bgcolor: "grey.100",
                        fontStyle: "italic",
                    }}
                >
                    {line.slice(2)}
                </Paper>
            );
        }

        // BULLET LIST
        else if (/^[\-\*\+] /.test(line)) {
            const items = [];
            while (i < lines.length && /^[\-\*\+] /.test(lines[i])) {
                items.push(lines[i].slice(2));
                i++;
            }
            elements.push(
                <List key={i} dense disablePadding sx={{ my: 1 }}>
                    {items.map((item, idx) => (
                        <ListItem key={idx} sx={{ display: "list-item", pl: 3 }}>
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: formatInline(item),
                                }}
                            />
                        </ListItem>
                    ))}
                </List>
            );
            continue;
        }

        // NUMBERED LIST
        else if (/^\d+\. /.test(line)) {
            const items = [];
            while (i < lines.length && /^\d+\. /.test(lines[i])) {
                items.push(lines[i].replace(/^\d+\. /, ""));
                i++;
            }
            elements.push(
                <List key={i} dense disablePadding sx={{ listStyle: "decimal", pl: 3, my: 1 }}>
                    {items.map((item, idx) => (
                        <ListItem key={idx} sx={{ display: "list-item" }}>
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: formatInline(item),
                                }}
                            />
                        </ListItem>
                    ))}
                </List>
            );
            continue;
        }

        // PARAGRAPH
        else if (line.trim() !== "") {
            elements.push(
                <Typography
                    key={i}
                    variant="body1"
                    sx={{ mb: 2, lineHeight: 1.7 }}
                    dangerouslySetInnerHTML={{ __html: formatInline(line) }}
                />
            );
        }

        i++;
    }

    return elements;
};



    const markdownContent = `# Feature Mapping & Data Flow Documentation

## Overview

This document outlines how various features in the indoor navigation Flutter module interact with each other and utilize data from the different APIs. It provides a comprehensive view of data flow, feature dependencies, and system integration patterns.

---

## System Architecture Diagram

\`\`\`
┌─────────────┐
│  API Layer  │
└──────┬──────┘
       │
       ├─── Patch API ────────┐
       │                      │
       ├─── Polyline API ─────┤
       │                      ├──► Rendering System ──► Google Maps Layer
       ├─── Landmark API ─────┤                              │
       │                      │                              │
       ├─── Waypoint API ─────┤                              │
       │                      │                              ▼
       └─── Beacon API ───────┘                      User Interface
                                                            │
                                                            ├─── Map Visualization
                                                            ├─── Search Feature
                                                            ├─── Get Directions
                                                            └─── Turn-by-Turn Navigation
\`\`\`

---

## Feature 1: Map Visualization & Rendering

### Description
Renders the building layout on Google Maps as the base map layer, providing users with a visual representation of the indoor space.

### API Dependencies
- **Patch API** (Primary)
- **Polyline API** (Primary)

### Data Flow

\`\`\`
Patch API
   │
   ├──► Returns: Global reference coordinates
   │
   ▼
Coordinate System Established
   │
   ▼
Polyline API
   │
   ├──► Returns: Polygon data (rooms, washrooms, offices)
   ├──► Returns: Polyline data (walls, boundaries)
   │
   ▼
Rendering System Processing
   │
   ├──► Transform coordinates using Patch reference
   ├──► Generate drawable shapes from polygon/polyline data
   ├──► Apply styling and layering
   │
   ▼
Google Maps Overlay
   │
   └──► Display: Building floor plan overlaid on base map
\`\`\`

### Feature Interactions
- Provides the base layer for all other features
- Polygon boundaries used for tap detection in Search functionality
- Visual reference for navigation paths in Get Directions feature
- Background context for Turn-by-Turn navigation

---

## Feature 2: Interactive Polygon Selection & Details

### Description
Allows users to tap on rendered polygons to view detailed information about that specific location within the venue.

### API Dependencies
- **Polyline API** (For polygon boundaries)
- **Landmark API** (Primary)

### Data Flow

\`\`\`
User Interaction
   │
   └──► User taps on polygon in map view
         │
         ▼
      Tap Detection System
         │
         ├──► Identify which polygon was tapped
         ├──► Extract polygon ID
         │
         ▼
      Landmark API Data Query
         │
         ├──► Search for landmarks associated with polygon ID
         ├──► Retrieve landmark details
         │
         ▼
      Display Information Panel
         │
         └──► Show: Landmark name, description, category, amenities
\`\`\`

### Feature Interactions
- **Input:** Rendered polygons from Map Visualization feature
- **Output:** Landmark details that can be used as destination in Get Directions
- **Triggers:** Can initiate Get Directions feature from detail view

---

## Feature 3: Search Functionality

### Description
Enables users to search through all available landmarks within the venue to find specific locations, amenities, or points of interest.

### API Dependencies
- **Landmark API** (Primary)

### Data Flow

\`\`\`
Landmark API
   │
   ├──► Returns: Complete list of landmarks
   ├──► Data includes: Names, descriptions, categories, coordinates
   │
   ▼
Search Index Creation
   │
   ├──► Index landmarks by name
   ├──► Index landmarks by category
   ├──► Index landmarks by keywords
   │
   ▼
User Search Input
   │
   └──► User enters search query
         │
         ▼
      Search Algorithm
         │
         ├──► Match query against indexed data
         ├──► Rank results by relevance
         ├──► Filter by floor
         │
         ▼
      Display Search Results
         │
         ├──► Show matched landmarks
         ├──► Display location on map
         │
         ▼
      User Selects Result
         │
         └──► Trigger: Get Directions or Show Details
\`\`\`

### Feature Interactions
- **Output:** Selected landmark becomes destination for Get Directions
- **Integrates with:** Map Visualization (highlights selected landmark)
- **Triggers:** Interactive Polygon Selection (shows landmark details)

---

## Feature 4: Get Directions (Path Computation)

### Description
Calculates and displays the optimal route from the user's current location to a selected destination landmark using on-device path computation.

### API Dependencies
- **Waypoint API** (Primary)
- **Beacon API** (For current location detection)
- **Landmark API** (For destination coordinates)

### Data Flow

\`\`\`
Waypoint API
   │
   ├──► Returns: Complete path graph
   ├──► Nodes: Waypoint coordinates
   ├──► Edges: Connections with costs
   │
   ▼
Path Graph Construction
   │
   ├──► Build in-memory graph structure
   ├──► Assign weights based on distance/accessibility
   │
   ▼
User Requests Directions
   │
   ├──► Source: Current location (from Beacon positioning)
   ├──► Destination: Selected landmark coordinates
   │
   ▼
Path Computation (On-Device)
   │
   ├──► Find nearest waypoint to source
   ├──► Find nearest waypoint to destination
   ├──► Calculate optimal path through graph
   │
   ▼
Path Processing
   │
   ├──► Generate step-by-step instructions
   ├──► Calculate total distance
   ├──► Estimate travel time
   │
   ▼
Display Path on Map
   │
   ├──► Draw route line on map overlay
   ├──► Highlight navigation waypoints
   └──► Show route summary (distance, time, floors)
\`\`\`

### Feature Interactions
- **Input from:** Search Feature (destination selection)
- **Input from:** Interactive Polygon Selection (destination selection)
- **Integrates with:** Map Visualization (path overlay)
- **Triggers:** Turn-by-Turn Navigation feature
- **Uses:** Beacon positioning for source location
---

## Feature 5: Turn-by-Turn Navigation

### Description
Provides real-time navigation guidance with step-by-step instructions as users move from their source location to the destination within the venue.

### API Dependencies
- **Waypoint API** (For navigation path)
- **Beacon API** (Primary for real-time positioning)
- **Landmark API** (For destination and landmark references)

### Data Flow

\`\`\`
Pre-requisite: Computed Path from Get Directions
   │
   ├──► Path waypoints sequence
   ├──► Step-by-step instructions
   │
   ▼
Navigation Session Starts
   │
   ▼
Beacon API Data
   │
   ├──► Returns: Beacon positions and IDs
   │
   ▼
Real-Time Position Detection Loop
   │
   ├──► Calculate position
   │    │
   │    ▼
   │  Current Location Update
   │    │
   │    ├──► Compare with path waypoints
   │    ├──► Determine next instruction
   │    ├──► Calculate remaining distance
   │    │
   │    ▼
   │  Navigation Guidance Display
   │    │
   │    ├──► Show current instruction (turn left, go straight, etc.)
   │    ├──► Display distance to next turn
   │    ├──► Update progress on map
   │    ├──► Voice announcements
   │    │
   │    ▼
   │    │
   └────┘ (Loop continues)
       │
       ▼
Destination Reached
   │
   └──► End navigation session
        └──► Display completion message
\`\`\`

### Feature Interactions
- **Requires:** Completed path from Get Directions feature
- **Integrates with:** Map Visualization (shows current position and path)
- **Uses:** Real-time beacon positioning for location tracking
- **Can trigger:** Reroute via Get Directions if user deviates

---

## Cross-Feature Data Dependencies Matrix

| Feature | Patch API | Polyline API | Landmark API | Waypoint API | Beacon API |
|---------|-----------|--------------|--------------|--------------|------------|
| **Map Visualization** | ✓ Primary | ✓ Primary | - | - | - |
| **Polygon Selection** | ✓ Primary | ✓ Reference | ✓ Primary | - | - |
| **Search** | - | - | ✓ Primary | - | - |
| **Get Directions** | - | - | ✓ Destination | ✓ Primary | ✓ Optional |
| **Turn-by-Turn Nav** | - | - | ✓ Reference | ✓ Path Data | ✓ Primary |

---

## Feature Integration Workflows

### Workflow 1: Complete Navigation Journey

\`\`\`
1. User opens app
   └──► Map Visualization loads building layout
        
2. User searches for "Restroom"
   └──► Search Feature queries Landmark API
        └──► Displays results
        
3. User selects destination
   └──► Map Visualization highlights location
        └──► Shows landmark details from Interactive Polygon Selection
        
4. User taps "Get Directions"
   └──► Get Directions computes path using Waypoint graph
        └──► Displays route on map
        
5. User taps "Start Navigation"
   └──► Turn-by-Turn Navigation begins
        └──► Beacon positioning tracks user movement
             └──► Provides real-time guidance
             
6. User reaches destination
   └──► Navigation ends
        └──► Returns to normal map view
\`\`\`

### Workflow 2: Explore & Discover

\`\`\`
1. User browses map
   └──► Map Visualization displays building
        
2. User taps interesting polygon
   └──► Interactive Polygon Selection shows details
        └──► Landmark information displayed
        
3. User decides to visit
   └──► Taps "Get Directions" from detail panel
        └──► Proceeds to navigation
\`\`\`
---

This feature mapping demonstrates how a cohesive indoor navigation system is built by orchestrating multiple APIs and features. Each feature serves a specific purpose while seamlessly integrating with others to provide a comprehensive user experience. The on-device path computation ensures fast response times, while beacon-based positioning enables accurate real-time navigation throughout the venue.

----

# API Overview - Indoor Navigation Flutter Module

## Introduction

This document provides an overview of the APIs integrated into the Flutter module for indoor navigation and mapping functionality. The module utilizes five core APIs that work together to provide comprehensive indoor positioning, navigation, and location services within building environments.

---

## API Summary

| API Name | Purpose | Primary Use Case |
|----------|---------|------------------|
| Patch API | Global Reference System | Building coordinate system establishment |
| Polyline API | Spatial Mapping | Room and area boundary definitions |
| Landmark API | Point of Interest | Location markers and navigation targets |
| Waypoint API | Path Network | Navigable route graph generation |
| Beacon API | Positioning Infrastructure | Indoor positioning beacon management |

---

## Detailed API Descriptions

### 1. Patch API

**Purpose:** Establishes the global reference coordinate system for the building

**Functionality:**
- Provides the foundational geospatial reference frame for the entire building structure
- Returns global positioning data that anchors the building to real-world coordinates
- Enables accurate transformation between local building coordinates and global positioning systems

**Key Data Retrieved:**
- Building origin coordinates
- Orientation and alignment data
- Scale and projection information
- Reference coordinate system parameters

**Use Case:** This is typically the first API called during initialization, as all other spatial data depends on this reference frame.

---

### 2. Polyline API

**Purpose:** Retrieves mapped polygons and polylines representing physical spaces

**Functionality:**
- Provides geometric definitions of rooms, corridors, and other spatial boundaries
- Returns polygon data for enclosed spaces (rooms, washrooms, offices, etc.)
- Supplies polyline data for linear features (walls, boundaries, pathways)
- Maps the physical layout of the building in vector format

**Key Data Retrieved:**
- Polygon coordinates for rooms and enclosed areas
- Polyline coordinates for boundaries and walls
- Area identifiers and spatial relationships
- Floor-level associations

**Use Case:** Essential for rendering building floor plans and visualizing the venue layout.

---

### 3. Landmark API

**Purpose:** Manages points of interest and navigation landmarks within the venue

**Functionality:**
- Retrieves details about tagged landmarks associated with polygons from the Polyline API
- Provides information about standalone landmarks annotated throughout the venue
- Returns metadata for navigation targets and points of interest

**Key Data Retrieved:**
- Landmark names and descriptions
- Associated polygon/area references
- Landmark coordinates and floor levels
- Categories and types (entrances, exits, amenities, etc.)
- Navigation-relevant attributes

**Use Case:** Powers search functionality, navigation destination selection, and provides contextual information about locations within the building.

---

### 4. Waypoint API

**Purpose:** Provides the hypermapped path network for navigation

**Functionality:**
- Returns a graph structure of all possible navigable paths within the building
- Defines connections between different areas and floors
- Supplies routing nodes for path calculation
- Includes accessibility and path characteristic information

**Key Data Retrieved:**
- Waypoint nodes with coordinates
- Edge connections between waypoints
- Path accessibility attributes (stairs, elevators, ramps)
- Floor transition points

**Use Case:** Core API for route calculation, turn-by-turn navigation, and generating optimal paths between any two points in the building.

---

### 5. Beacon API

**Purpose:** Manages indoor positioning beacon infrastructure

**Functionality:**
- Provides data about physical beacons deployed throughout the building
- Returns beacon location, identification, and configuration details

**Key Data Retrieved:**
- Beacon unique identifiers
- Physical coordinates of each beacon
- Floor and zone associations

**Use Case:** Critical for real-time indoor positioning by detecting nearby beacons and calculating user position

---

## API Integration Flow

\`\`\`
1. Initialize with Patch API
   ↓
2. Load building layout (Polyline API)
   ↓
3. Load landmarks and POIs (Landmark API)
   ↓
4. Build navigation graph (Waypoint API)
   ↓
5. Configure positioning (Beacon API)
   ↓
6. Ready for navigation and positioning
\`\`\`

---

These five APIs form a comprehensive indoor navigation system, providing everything needed for mapping, positioning, and navigation within building environments. Each API serves a distinct purpose while working cohesively to deliver a complete indoor location-based service solution.

---

# Flutter Module Integration Guide

This guide provides step-by-step instructions for integrating a Flutter module into your existing Android and iOS native applications.

---

## Table of Contents

- [Overview](#overview)
- [Prerequisites](#prerequisites)
- [Android Integration (Module Folder)](#android-integration-module-folder)
- [Android Integration (AAR)](#android-integration-aar)
- [Troubleshooting](#troubleshooting)

---

## Overview

This guide demonstrates how to integrate a Flutter module into existing native Android and iOS applications using

### AAR Method
- Uses compiled Android Archive files (recommended for production/CI/CD)
---

## Prerequisites

### For Android Development
- Android Studio (Arctic Fox or later)
- Gradle 8.6+
- Kotlin 1.9.23
- JDK 17
- Flutter SDK

### For iOS Development
- Xcode 14.0+
- CocoaPods 1.10+
- iOS deployment target 14.0+
- Flutter SDK

---

### Step 1: Configure \`settings.gradle.kts\`

Add the following code to include the Flutter module:

**For Kotlin DSL (\`settings.gradle.kts\`):**

\`\`\`kotlin
pluginManagement {
    repositories {
        google()
        mavenCentral()
        gradlePluginPortal()
        maven {
            url = uri("https://storage.googleapis.com/download.flutter.io")
        }
        maven {
            url = uri("/path/to/my_flutter_module_repo")
        }
    }
}

dependencyResolutionManagement {
    repositoriesMode.set(RepositoriesMode.PREFER_PROJECT)
    repositories {
        google()
        mavenCentral()
        maven { url = uri("https://jitpack.io") }
        maven { url = uri("https://storage.googleapis.com/download.flutter.io") }
        maven {url = uri("/path/to/my_flutter_module_repo")}
    }
}

rootProject.name = "YourAppName"
include(":app")
\`\`\`
**For Groovy (\`settings.gradle\`):**

\`\`\`kotlin
pluginManagement {
    repositories {
        google()
        mavenCentral()
        gradlePluginPortal()
        maven {
            url "https://storage.googleapis.com/download.flutter.io"
        }
        maven {
            url "/path/to/my_flutter_module_repo"
        }
    }
}

dependencyResolutionManagement {
    repositoriesMode.set(RepositoriesMode.PREFER_PROJECT)
    repositories {
        google()
        mavenCentral()
        maven { url "https://jitpack.io" }
        maven { url "https://storage.googleapis.com/download.flutter.io" }
        maven { url "/path/to/my_flutter_module_repo" }
    }
}

rootProject.name = "YourAppName"
include ':app'
\`\`\`

> **Note:** Update the path to your Flutter module's repo folder.

### Step 2: Update \`gradle.properties\`

\`\`\`properties
org.gradle.jvmargs=-Xmx2048m -Dfile.encoding=UTF-8
android.useAndroidX=true
android.enableJetifier=true
kotlin.incremental=true
kotlin.code.style=official
android.nonTransitiveRClass=true
\`\`\`

### Step 3: Update \`gradle-wrapper.properties\`

\`\`\`properties
distributionBase=GRADLE_USER_HOME
distributionPath=wrapper/dists
distributionUrl=https\://services.gradle.org/distributions/gradle-8.6-bin.zip
zipStoreBase=GRADLE_USER_HOME
zipStorePath=wrapper/dists
\`\`\`

### Step 4: Configure \`build.gradle.kts\` (App Level)
In your root \`build.gradle.kts\`, add:

**For Kotlin DSL:**

\`\`\`kotlin
plugins {
    id("com.android.application")
    id("org.jetbrains.kotlin.android")
}

configurations.all {
    resolutionStrategy {
        force("androidx.appcompat:appcompat:1.7.0")
        force("com.google.android.material:material:1.12.0")
    }
}

android {
    namespace = "com.example.yourapp"
    compileSdk = 36
    
    defaultConfig {
        applicationId = "com.example.yourapp"
        minSdk = 23
        targetSdk = 36
        versionCode = 1
        versionName = "1.0"
    }
    
    compileOptions {
        sourceCompatibility = JavaVersion.VERSION_17
        targetCompatibility = JavaVersion.VERSION_17
    }
    
    kotlinOptions {
        jvmTarget = "17"
    }
    
    buildFeatures {
        viewBinding = true
    }
}

dependencies {
    implementation("androidx.activity:activity:1.9.2")
    implementation("androidx.appcompat:appcompat:1.7.0")
    implementation("com.google.android.material:material:1.12.0")
    
    // Flutter module dependency
    implementation("com.example.my_flutter_module:flutter_release:1.0")
}
\`\`\`
**For Groovy:** 

\`\`\`groovy
plugins {
    id 'com.android.application'
    id 'org.jetbrains.kotlin.android'
}

configurations.all {
    resolutionStrategy {
        force 'androidx.appcompat:appcompat:1.7.0'
        force 'com.google.android.material:material:1.12.0'
    }
}

android {
    namespace 'com.example.yourapp'
    compileSdk 36
    
    defaultConfig {
        applicationId 'com.example.yourapp'
        minSdk 23
        targetSdk 36
        versionCode 1
        versionName '1.0'
    }
    
    compileOptions {
        sourceCompatibility JavaVersion.VERSION_17
        targetCompatibility JavaVersion.VERSION_17
    }
    
    kotlinOptions {
        jvmTarget '17'
    }
    
    buildFeatures {
        viewBinding true
    }
}

dependencies {
    implementation 'androidx.activity:activity:1.9.2'
    implementation 'androidx.appcompat:appcompat:1.7.0'
    implementation 'com.google.android.material:material:1.12.0'
    
    // Flutter module dependency
    implementation 'com.example.my_flutter_module:flutter_release:1.0'
}
\`\`\`

### Step 5: Initialize Flutter Engine in \`MainActivity.kt\`

\`\`\`kotlin
class MainActivity : AppCompatActivity() {
    private var flutterEngine: FlutterEngine? = null
    
    override fun onCreate(savedInstanceState: Bundle?) {
        super.onCreate(savedInstanceState)
        
        // Create and configure Flutter engine
        flutterEngine = FlutterEngine(this)
        flutterEngine!!.dartExecutor.executeDartEntrypoint(
            DartExecutor.DartEntrypoint.createDefault()
        )
        
        // Cache the engine
        FlutterEngineCache
            .getInstance()
            .put("my_flutter_engine", flutterEngine!!)
        
        // Launch Flutter Activity
        startActivity(
            FlutterActivity
                .withCachedEngine("my_flutter_engine")
                .build(this)
        )
    }
}
\`\`\`

### Step 6: Update \`AndroidManifest.xml\`

Inside the \`<application>\` tag:

\`\`\`xml
<activity
    android:name="io.flutter.embedding.android.FlutterActivity"
    android:configChanges="orientation|keyboardHidden|keyboard|screenSize|locale|layoutDirection|fontScale|screenLayout|density|uiMode"
    android:hardwareAccelerated="true"
    android:windowSoftInputMode="adjustResize"
    android:exported="false" />
\`\`\`

### Step 7: Build Commands

\`\`\`bash
./gradlew clean
./gradlew installDebug
\`\`\`
---

## API Key Integration (Android)

### Step 1: Add API Key to \`local.properties\`

\`\`\`properties
API_KEY= Your_API_Key
\`\`\`

### Step 2: Configure \`build.gradle.kts\` (App Level)

Add at the top of the file:

\`\`\`kotlin
// Read API_KEY from local.properties
val localProperties = Properties()
val localPropertiesFile = rootProject.file("local.properties")
if (localPropertiesFile.exists()) {
    localPropertiesFile.inputStream().use { localProperties.load(it) }
}
val apiKey: String = localProperties.getProperty("API_KEY", "")
\`\`\`

Inside the \`android\` block:

\`\`\`kotlin
android {
    buildFeatures {
        buildConfig = true
    }
    
    defaultConfig {
        buildConfigField("String", "API_KEY", "\"\$apiKey\"")
    }
}
\`\`\`

### Step 3: Setup Method Channel in \`MainActivity.kt\`

\`\`\`kotlin
class MainActivity : AppCompatActivity() {
    private val CHANNEL = "com.example.yourpackage/config"
    
    override fun onCreate(savedInstanceState: Bundle?) {
        super.onCreate(savedInstanceState)
        
        val flutterEngine = FlutterEngine(this)
        flutterEngine.dartExecutor.executeDartEntrypoint(
            DartExecutor.DartEntrypoint.createDefault()
        )
        
        // Setup MethodChannel
        MethodChannel(flutterEngine.dartExecutor.binaryMessenger, CHANNEL)
            .setMethodCallHandler { call, result ->
                if (call.method == "getApiKey") {
                    result.success(BuildConfig.API_KEY)
                } else {
                    result.notImplemented()
                }
            }
        
        // Cache engine
        FlutterEngineCache
            .getInstance()
            .put("my_engine_id", flutterEngine)
        
        // Launch FlutterActivity
        val flutterIntent = FlutterActivity
            .withCachedEngine("my_engine_id")
            .build(this)
        
        startActivity(flutterIntent)
        finish()
    }
}
\`\`\`

---

## Troubleshooting

### Android Issues

#### Kotlin Version Mismatch

Ensure Kotlin version \`1.9.23\` and Compose compiler \`1.5.13\`:

\`\`\`kotlin
composeOptions {
    kotlinCompilerExtensionVersion = "1.5.13"
}
\`\`\`

#### Desugaring Issue

Enable core library desugaring:

\`\`\`kotlin
compileOptions {
    isCoreLibraryDesugaringEnabled = true
}

dependencies {
    coreLibraryDesugaring("com.android.tools:desugar_jdk_libs:2.0.4")
}
\`\`\`

#### Flutter Gradle Plugin Repository Error

If you see:

\`\`\`
Failed to apply plugin 'dev.flutter.flutter-gradle-plugin'
Build was configured to prefer settings repositories over project repositories
\`\`\`

**Solution:** In \`settings.gradle.kts\`, change:

\`\`\`kotlin
dependencyResolutionManagement {
    //repositoriesMode.set(RepositoriesMode.FAIL_ON_PROJECT_REPOS) (Comment this line)
    repositoriesMode.set(RepositoriesMode.PREFER_SETTINGS)
    repositories {
        google()
        mavenCentral()
    }
}
\`\`\`

#### MultipleCompilationErrorsException

If you encounter Groovy compilation errors:

\`\`\`bash
rm -rf .gradle
rm -rf ~/.gradle/caches
rm -rf ~/.gradle/kotlin-dsl
rm -rf ~/.gradle/daemon
./gradlew clean
\`\`\`

Then restart Android Studio and rebuild.

### iOS Issues

#### Pod Install Fails

\`\`\`bash
# Clear CocoaPods cache
pod cache clean --all
pod deintegrate
pod install --repo-update
\`\`\`

#### Xcode Build Fails

1. Clean build folder: \`Product > Clean Build Folder\` (Cmd+Shift+K)
2. Delete derived data:
\`\`\`bash
rm -rf ~/Library/Developer/Xcode/DerivedData
\`\`\`
3. Reinstall pods

#### Module Not Found Error

Ensure you're opening the \`.xcworkspace\` file, not \`.xcodeproj\`.

---

## Best Practices


### 1. Dependency Management

- **Pin Flutter module version** in production
- Use specific versions for all native dependencies
- Regularly update Flutter SDK and dependencies

### 2. Security Best Practices

- **Never commit API keys** to version control
- Use environment variables or secure storage

### 3. Memory Management

- **Dispose Flutter engines** when not needed:

\`\`\`kotlin
// Android
flutterEngine.destroy()
\`\`\`

\`\`\`swift
// iOS
flutterEngine.destroyContext()
\`\`\`

- Use cached engines for frequently accessed screens
- Monitor memory usage during development

### 4. Error Handling

Implement proper error handling for Flutter initialization:

\`\`\`kotlin
// Android
try {
    val intent = FlutterActivity.createDefaultIntent(this)
    startActivity(intent)
} catch (e: Exception) {
    Log.e("FlutterError", "Failed to launch Flutter: \${e.message}")
    // Show fallback UI or error message
}
\`\`\`

\`\`\`swift
// iOS
do {
    flutterEngine.run()
    let flutterVC = FlutterViewController(engine: flutterEngine, nibName: nil, bundle: nil)
    present(flutterVC, animated: true)
} catch {
    print("Failed to launch Flutter: \(error)")
    // Show error alert
}
\`\`\`
---

**Last Updated:** November 2025  
**Flutter Version:** 3.16+  
**Minimum Android SDK:** 23  
**Minimum iOS Version:** 14.0`;

    const handleAnchorClick = (e) => {
    const link = e.target.closest("a.internal-anchor");
    if (!link) return;

    e.preventDefault();
    const id = link.getAttribute("href").substring(1);
    const target = document.getElementById(id);

    if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
};

    return (
        <Box
    sx={{ height: "100%", overflowY: "auto", p: 2 }}
    onClick={handleAnchorClick}
>
    {parseMarkdown(markdownContent)}
</Box>

    );
}

